import React from 'react';
import _ from 'lodash';

import AttachDocument from '../forms/AttachDocument';
import { DateInput } from '../DateInput';
import ComboBox from '../combobox/ComboBox';
import {
  FormListItemGroup,
  FormListItemChild,
  FormElement,
} from '../CommonFormComponents';

import {
  deleteControlCode,
  isNullOrUndefined,
  isNullOrUndefinedOrEmptyStr,
} from '../../utilities/stringUtil';
import { PERIOD_KIND } from '../../utilities/constants';
import { messageUtility } from '../../utilities/messageUtility';

/**
 * 職種・免許・資格（入力ブロック）
 * @props {Array} valueArray: valueの配列
 * @props {Object} keyObject: Object型でKeyの名称が格納されている
 *  @param required {String} searchKey: リストボックスで検索するときに必要なプロパティ名（その他の用途でも使用されている）
 *  @param required {String} targetKey: 値を格納したいプロパティ名
 *  @param required {String} takeDateKey: 取得年月日で使用するプロパティ名
 *  @optional {String} codeKey: 『免許・資格』コードプロパティ名
 *
 * @props {Object} comboBoxObject: 『免許・資格』リスト項目
 *  @param required {Boolean} isShow: comboBoxの項目を表示したいか？
 *  @param required {Array} comboBoxList: リストボックス内要素（現状『免許・資格』リスト）
 *  @param required {String} comboBoxTitle: コンボボックス名称
 *
 * @props {Object} licenseNoObject: 免許資格番号項目
 *  @param required {Boolean} isShow: 免許資格番号項目を表示したいか？
 *  @param required {String} propertyName: 免許資格番号のプロパティ名
 *  @param required {String} licenseNoTitle: 免許資格番号
 *
 * @props {Object} takeDateObject: 取得年月日項目
 *  @param required {Boolean} isShow: 取得年月日項目を表示したいか？
 *  @param required {String} takeDateTitle: 取得年月日名称
 *
 * @props {Object} expireDateObject: 有効期限年月日項目
 *  @param required {Boolean} isShow: 有効期限年月日項目を表示したいか？
 *  @param required {Boolean} isShowInfo: 有効期限年月日項目の備考を表示したいか？
 *  @param required {String} takeDateTitle: 有効期限年月日
 *
 * @props {Object} validPeriodObject: 有効期間
 *  @param required {Boolean} isShow: 有効期間項目を表示したいか？
 *  @param required {String} validPeriodTitle: 有効期間
 *
 * @props {Object} attachDocumentObject: 証明書・免許証項目
 *  @param required {Boolean} isShow: 証明書・免許証項目を表示したいか？
 *  @param required {Array} documentCodes: ファイル添付 免許資格 証明書・免許証 選択項目
 *  @param required {String} itemName: @TODO: 詳細は要調査
 *  @param required {String} indexId: @TODO: 詳細は要調査
 *  @param required {String} labelDocument: @TODO: 詳細は要調査
 *  @param required {String} keyName: @TODO: 詳細は要調査
 *
 * @props {Array} messageArray: バリデーションエラーメッセージの配列
 * @props {Boolean} disabled: 詳細表示 or 編集画面表示
 * @props {Boolean} safetyDocItem: 『安全書類項目』アイコンを表示するか？
 * @props {Function} events: メソッド群
 */
const LicenseRegisterBlock = ({
  valueArray,
  keyObject,
  comboBoxObject = null,
  licenseNoObject = null,
  takeDateObject = null,
  expireDateObject = null,
  validPeriodObject = null,
  attachDocumentObject = null,
  messageArray,
  disabled,
  safetyDocItem = false,
  events,
}) => {
  const { searchKey, targetKey, takeDateKey, expireDateKey } = keyObject;
  return _.map(valueArray, (value, index) => {
    // 免許・資格名称リストのエラーメッセージを取得
    const errorMessage = _.get(messageArray, `[${index}].${searchKey}`);
    // 免許資格番号のエラーメッセージを取得
    const errorMessageForInputItems =
      isNullOrUndefined(licenseNoObject) === false &&
      _.get(messageArray, `[${index}].${licenseNoObject.propertyName}`);
    // 免許・資格取得年月日のエラーメッセージを取得
    const errorMessageForTakeDateInputItems = _.get(
      messageArray,
      `[${index}].${takeDateKey}`
    );
    // 免許・資格有効期限年月日のエラーメッセージを取得
    const errorMessageForExpireDateInputItems = _.get(
      messageArray,
      `[${index}].${expireDateKey}`
    );
    // 免許・資格有効期限年月日の警告メッセージを取得
    const warningMessageForExpireDateInputItems = _.get(
      value,
      'warningMessage'
    );

    // preview表示中か？
    const isPreview = disabled === true;

    return (
      <FormListItemGroup
        closeFn={
          isPreview
            ? false
            : () => {
                events.deleteValue([...valueArray], index, keyObject);
              }
        }
        key={index}
        isBorder={false}
      >
        {isNullOrUndefined(comboBoxObject) === false &&
          comboBoxObject.isShow === true && (
            <FormListItemChild
              label={comboBoxObject.comboBoxTitle}
              safetyDocItem={safetyDocItem}
            >
              <FormElement validate={errorMessage}>
                <div
                  className={`form-list__element-select ${
                    disabled ? 'form-list__element-select--disabled' : ''
                  }`}
                >
                  <ComboBox
                    listId={`${targetKey}-list`}
                    disabled={disabled}
                    value={value[searchKey]}
                    comboBoxList={comboBoxObject.comboBoxList}
                    comboBoxValueKey={searchKey}
                    onChange={e => {
                      events.inputValueSearch(
                        [...valueArray],
                        comboBoxObject.comboBoxList,
                        deleteControlCode(e.target.value),
                        index,
                        keyObject
                      );
                    }}
                  />
                </div>
              </FormElement>
            </FormListItemChild>
          )}

        {isNullOrUndefined(licenseNoObject) === false &&
          licenseNoObject.isShow === true && (
            <FormListItemChild label={licenseNoObject.licenseNoTitle}>
              <FormElement validate={errorMessageForInputItems}>
                {disabled ? (
                  <span>{valueArray[index][licenseNoObject.propertyName]}</span>
                ) : (
                  <input
                    id={licenseNoObject.propertyName}
                    type='text'
                    name={licenseNoObject.propertyName}
                    disabled={disabled}
                    value={
                      valueArray[index][licenseNoObject.propertyName] || ''
                    }
                    onChange={e => {
                      const clonedValues = _.cloneDeep(valueArray);
                      clonedValues[index] = {
                        ...clonedValues[index],
                        [e.target.name]: deleteControlCode(e.target.value),
                      };
                      events.setJobValues(targetKey, clonedValues, index);
                    }}
                  />
                )}
              </FormElement>
            </FormListItemChild>
          )}

        {isNullOrUndefined(takeDateObject) === false &&
          takeDateObject.isShow === true && (
            <FormListItemChild label={takeDateObject.takeDateTitle}>
              {disabled && !valueArray[index][takeDateKey] ? null : (
                <DateInput
                  isPreview={isPreview}
                  disabled={disabled}
                  jpDelimiterChar={true}
                  dateString={valueArray[index][takeDateKey]}
                  message={errorMessageForTakeDateInputItems}
                  onChangeValue={val => {
                    const clonedValues = _.cloneDeep(valueArray);
                    clonedValues[index][takeDateKey] = val;
                    events.setJobValues(targetKey, clonedValues, index);
                  }}
                />
              )}
            </FormListItemChild>
          )}

        {isNullOrUndefined(validPeriodObject) === false &&
          validPeriodObject.isShow === true && (
            <FormListItemChild label={validPeriodObject.validPeriodTitle}>
              <FormElement>
                {_.has(value, 'validPeriodObject') === true &&
                  isNullOrUndefinedOrEmptyStr(
                    value.validPeriodObject.period_kind
                  ) === false &&
                  _.map(PERIOD_KIND, it => {
                    if (
                      it.ID === value.validPeriodObject.period_kind &&
                      value.validPeriodObject.period_kind ===
                        PERIOD_KIND.LIFETIME_ENTITLEMENT.ID
                    ) {
                      return <span key={it.ID}>{it.NAME}</span>;
                    }
                    if (it.ID === value.validPeriodObject.period_kind) {
                      return (
                        <React.Fragment key={it.ID}>
                          <span className='form-list__element--auto-calculator-label'>
                            {value.validPeriodObject.valid_period}
                            {it.NAME}
                          </span>
                          {isPreview === false && (
                            <button
                              className='form-list__element--auto-calculator-button btn-fixedsize--stay search-form__btn'
                              onClick={() => {
                                events.calculationExpiredDate(
                                  value,
                                  index,
                                  targetKey
                                );
                              }}
                            >
                              有効期限を自動入力
                            </button>
                          )}
                        </React.Fragment>
                      );
                    }
                  })}
              </FormElement>
            </FormListItemChild>
          )}

        {isNullOrUndefined(expireDateObject) === false &&
          expireDateObject.isShow === true &&
          value.showExpireDate === true && (
            <FormListItemChild label={expireDateObject.expireDateTitle}>
              {disabled && !valueArray[index][expireDateKey] ? null : (
                <DateInput
                  isPreview={isPreview}
                  disabled={disabled}
                  jpDelimiterChar={true}
                  dateString={valueArray[index][expireDateKey]}
                  message={errorMessageForExpireDateInputItems}
                  warning={
                    isNullOrUndefinedOrEmptyStr(
                      errorMessageForTakeDateInputItems
                    ) === true &&
                    isNullOrUndefinedOrEmptyStr(
                      warningMessageForExpireDateInputItems
                    ) === false
                      ? warningMessageForExpireDateInputItems
                      : null
                  }
                  info={
                    disabled === false &&
                    expireDateObject.isShowInfo === true &&
                    messageUtility.getMessage('I-000-158')
                  }
                  onChangeValue={val => {
                    const clonedValues = _.cloneDeep(valueArray);
                    clonedValues[index][expireDateKey] = val;
                    events.setJobValues(targetKey, clonedValues, index);
                  }}
                />
              )}
            </FormListItemChild>
          )}

        {isNullOrUndefined(attachDocumentObject) === false &&
          attachDocumentObject.isShow === true && (
            <FormListItemChild label={attachDocumentObject.labelDocument}>
              <FormElement>
                <AttachDocument
                  companyDocuments={valueArray[index].worker_documents}
                  documentCodes={attachDocumentObject.documentCodes}
                  events={events}
                  readOnly={disabled}
                  itemName={attachDocumentObject.itemName}
                  indexId={attachDocumentObject.indexId}
                  isFormListDefault={true}
                  index={index}
                  keyName={attachDocumentObject.keyName}
                />
              </FormElement>
            </FormListItemChild>
          )}
      </FormListItemGroup>
    );
  });
};

export default LicenseRegisterBlock;



// WEBPACK FOOTER //
// ./src/components/worker/LicenseRegisterBlock.jsx